import './mailCodePage.scss';

import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, uniqueId } from 'lodash';
import { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import logo from '../../../assets/logo-default.svg';
import vehicleSUVTopView from '../../../assets/vehicle-suv-topview.png';
import { CircularProgressFullScreen } from '../../../components/shared_new/CircularProgressFullScreen/CircularProgressFullScreen';
import useApiStore from '../../../store/apiStore/useApiStore';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useMailCodeStore from '../../../store/mailCodeStore/useMailCodeStore';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { mailCode: parameterMailCode } = useParameterStore();
  const {
    computed: { brand },
  } = useLeadChannelStore();
  const {
    computed: { shouldFetchMailCodeDataOnLoad },
    fetchMailCodeDataFailedMessage,
    isMailCodeNotFoundError,
    isMailCodeInvalidSourceError,
    mailCode,
    fetchMailCodeData,
  } = useMailCodeStore();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      mailCodeValue: shouldFetchMailCodeDataOnLoad
        ? parameterMailCode
        : mailCode,
    },
    delayError: 200,
  });

  const mailCodeValidation = {
    required: t('validation.mailCode.required'),
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { mailCodeValue } = values;

    useMailCodeStore.setState({
      mailCode: mailCodeValue,
    });

    await fetchMailCodeData(mailCodeValue);

    const { fetchMailCodeDataSuccess } = useMailCodeStore.getState();

    if (fetchMailCodeDataSuccess) {
      navigate('/mail/offer');
    }
  };

  useEffect(() => {
    if (isMailCodeInvalidSourceError) {
      navigate('/sign-up');
    }
  }, [isMailCodeInvalidSourceError]);

  useEffect(() => {
    if (shouldFetchMailCodeDataOnLoad) {
      handleSubmit(formSubmit)();
    }
  }, [shouldFetchMailCodeDataOnLoad]);

  // display a full-screen loading spinner while attempting to fetch mail code data on load.
  if (shouldFetchMailCodeDataOnLoad) {
    return <CircularProgressFullScreen />;
  }

  return (
    <div className="mail-code-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.mailCode.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
          </header>

          <main>
            <figure>
              <img
                alt={t('imageAlts.vehicleSUVTopView')}
                src={vehicleSUVTopView}
              />
            </figure>
            <form
              data-testid={locator.pages.mailCode.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.mailCode.form.h1}>
                  <Trans
                    components={[<u key={uniqueId('u-')} />]}
                    i18nKey={'MailCodePage.title'}
                    values={{
                      brand,
                    }}
                  />
                </h1>
                <h2 data-testid={locator.pages.mailCode.form.h2}>
                  <Trans
                    components={[<u key={uniqueId('u-')} />]}
                    i18nKey={'MailCodePage.subtitle'}
                  />
                </h2>

                {!isMailCodeNotFoundError &&
                  !isMailCodeInvalidSourceError &&
                  fetchMailCodeDataFailedMessage && (
                    <div style={{ marginBottom: '2rem' }}>
                      <Alert
                        data-testid={locator.pages.mailCode.alert.error}
                        icon={false}
                        severity="error"
                      >
                        {fetchMailCodeDataFailedMessage}
                      </Alert>
                    </div>
                  )}

                <div style={{ marginBottom: '3rem' }}>
                  <TextField
                    {...register('mailCodeValue', mailCodeValidation)}
                    error={
                      !isEmpty(errors.mailCodeValue) || isMailCodeNotFoundError
                    }
                    inputProps={{
                      'data-testid': locator.pages.mailCode.form.mailCodeInput,
                    }}
                    label={t('MailCodePage.savingsId')}
                    variant="outlined"
                    fullWidth
                  />

                  {(!isEmpty(errors.mailCodeValue) ||
                    isMailCodeNotFoundError) && (
                    <span
                      data-testid={
                        locator.pages.mailCode.form.mailCodeInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.mailCodeValue?.message) ||
                        fetchMailCodeDataFailedMessage}
                    </span>
                  )}
                </div>

                <LoadingButton
                  color="primary"
                  data-testid={locator.pages.mailCode.form.submitButton}
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('MailCodePage.continue')}
                </LoadingButton>

                <p className="form-sign-up">
                  <Link
                    data-testid={locator.pages.mailCode.form.signupLink}
                    to="/sign-up"
                  >
                    {t('MailCodePage.missingSavingsId')}
                  </Link>
                </p>
              </div>
              <footer data-testid={locator.pages.mailCode.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'MailCodePage';
